<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { memberActivitiesApi } from '@/api/member'
import { useSessionStore } from '@/stores/session'
import FloatGif from '@/assets/raffle/float.gif'

const DISMISS_STORAGE_KEY = 'lucky_wheel_float_dismissed_until'
const POSITION_STORAGE_KEY = 'lucky_wheel_float_bottom'
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000
const LONG_PRESS_MS = 500
const FLOAT_SIZE = 90
const MOVE_CANCEL_THRESHOLD = 10

const router = useRouter()
const sessionStore = useSessionStore()
const dismissed = ref(isDismissed())

const { data: activitiesData } = useQuery({
  queryKey: ['member-activities'],
  queryFn: async () => {
    const res = await memberActivitiesApi()
    return res.data
  },
  enabled: () => !!sessionStore.sessionId,
})

const luckyWheel = computed(() => activitiesData.value?.lucky_wheel ?? null)
const visible = computed(() => !!luckyWheel.value && !dismissed.value)

const bottomPx = ref(getInitialBottom())
const isDragging = ref(false)

let longPressTimer: number | null = null
let startPointerY = 0
let startBottom = 0
let suppressClick = false

function getDefaultBottom(): number {
  return 80
}

function getMinBottom(): number {
  return getDefaultBottom()
}

function getMaxBottom(): number {
  return Math.max(getMinBottom(), window.innerHeight - FLOAT_SIZE - 16)
}

function clampBottom(value: number): number {
  return Math.min(Math.max(value, getMinBottom()), getMaxBottom())
}

function getInitialBottom(): number {
  const saved = localStorage.getItem(POSITION_STORAGE_KEY)
  if (saved) {
    return clampBottom(Number(saved))
  }
  return getDefaultBottom()
}

function isDismissed(): boolean {
  const until = localStorage.getItem(DISMISS_STORAGE_KEY)
  if (!until) return false
  return Date.now() < Number(until)
}

function clearLongPressTimer() {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

function stopDragging() {
  isDragging.value = false
  document.removeEventListener('pointermove', onDocumentPointerMove)
  document.removeEventListener('pointerup', onDocumentPointerUp)
  document.removeEventListener('pointercancel', onDocumentPointerUp)
}

function onDocumentPointerMove(event: PointerEvent) {
  if (!isDragging.value) return

  event.preventDefault()
  const deltaY = event.clientY - startPointerY
  bottomPx.value = clampBottom(startBottom - deltaY)

  if (Math.abs(deltaY) > MOVE_CANCEL_THRESHOLD) {
    suppressClick = true
  }
}

function onDocumentPointerUp() {
  clearLongPressTimer()

  if (isDragging.value) {
    localStorage.setItem(POSITION_STORAGE_KEY, String(bottomPx.value))
    stopDragging()
    window.setTimeout(() => {
      suppressClick = false
    }, 0)
    return
  }

  suppressClick = false
}

function onFloatPointerMove(event: PointerEvent) {
  if (isDragging.value) return
  if (Math.abs(event.clientY - startPointerY) > MOVE_CANCEL_THRESHOLD) {
    clearLongPressTimer()
  }
}

function onFloatPointerDown(event: PointerEvent) {
  if (event.button !== 0) return

  startPointerY = event.clientY
  startBottom = bottomPx.value
  suppressClick = false

  clearLongPressTimer()
  longPressTimer = window.setTimeout(() => {
    isDragging.value = true
    suppressClick = true
    document.addEventListener('pointermove', onDocumentPointerMove, { passive: false })
    document.addEventListener('pointerup', onDocumentPointerUp)
    document.addEventListener('pointercancel', onDocumentPointerUp)
  }, LONG_PRESS_MS)
}

function onFloatPointerUp() {
  clearLongPressTimer()
}

function handleClick() {
  if (suppressClick || isDragging.value) return
  router.push('/activity/lucky-wheel')
}

function handleDismiss() {
  localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now() + DISMISS_DURATION_MS))
  dismissed.value = true
}

onBeforeUnmount(() => {
  clearLongPressTimer()
  stopDragging()
})
</script>

<template>
  <div
    v-if="visible"
    class="fixed right-1 z-50 select-none touch-none"
    :class="isDragging ? 'cursor-grabbing' : ''"
    :style="{ bottom: `${bottomPx}px` }"
    @pointerdown="onFloatPointerDown"
    @pointermove="onFloatPointerMove"
    @pointerup="onFloatPointerUp"
    @pointercancel="onFloatPointerUp"
  >
    <div class="relative size-[90px]">
      <button
        type="button"
        class="absolute -top-1 -right-1 z-10 flex size-5 items-center justify-center rounded-full bg-black/45 text-white active:opacity-80"
        aria-label="关闭"
        @click.stop="handleDismiss"
        @pointerdown.stop
      >
        <van-icon name="cross" size="16" class="text-white!" />
      </button>

      <button
        type="button"
        class="block size-[90px] transition-transform"
        :class="isDragging ? 'scale-105' : 'active:scale-95'"
        aria-label="幸运大转盘"
        @click="handleClick"
      >
        <img :src="FloatGif" alt="幸运大转盘" class="size-[90px] pointer-events-none" />
      </button>
    </div>
  </div>
</template>
